// Enterprise Hero Section - Premium Design
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Building2, TrendingDown, Shield } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { AuroraBackground } from '@/components/ui/aurora-background';

export const EnterpriseHero = () => {
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.1 });
  
  const kpiStats = [
    { value: '32%', label: 'Cost Reduction', icon: <TrendingDown className="w-4 h-4" /> },
    { value: '60%', label: 'Less Technical Debt', icon: <Zap className="w-4 h-4" /> },
    { value: '99.9%', label: 'Uptime SLA', icon: <Shield className="w-4 h-4" /> },
  ];
  
  return (
    <section 
      ref={elementRef}
      className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-[#0a0a0a]"
    >
      {/* Aurora Background */}
      <AuroraBackground />
      
      {/* Floating background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.1) 0%, transparent 70%)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, hsl(270 100% 50% / 0.1) 0%, transparent 70%)',
            animation: 'float 10s ease-in-out infinite reverse',
          }}
        />
      </div>
      
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 50% 0%, hsl(var(--primary) / 0.08) 0%, transparent 50%)',
        }}
      />
      
      {/* Content */}
      <div className="container-professional relative z-10 pt-32 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <Building2 className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Enterprise Solutions</span>
          </div>
          
          {/* Headline */}
          <h1 
            className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 100ms',
            }}
          >
            AI-Powered Systems{' '}
            <span className="text-primary">for Enterprise Scale</span>
          </h1>
          
          {/* Subheadline */}
          <p 
            className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 200ms',
            }}
          >
            From logistics optimization to intelligent CRMs. We build systems 
            that transform operations and deliver measurable ROI within 90 days.
          </p>
          
          {/* CTAs */}
          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 300ms',
            }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-primary hover:bg-[#00d4e6] text-white font-semibold transition-all duration-300 hover:-translate-y-0.5"
              style={{
                boxShadow: '0 4px 24px -6px hsl(var(--primary) / 0.5)',
              }}
            >
              Schedule Discovery Call
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#case-studies"
              className="inline-flex items-center gap-2 px-6 py-4 text-white/70 hover:text-white font-medium transition-colors"
            >
              View Case Studies
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          
          {/* KPI Stats */}
          <div 
            className="grid grid-cols-3 gap-4 max-w-2xl mx-auto"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 400ms',
            }}
          >
            {kpiStats.map((stat, index) => (
              <div 
                key={stat.label}
                className="group flex flex-col items-center p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-primary/30 hover:bg-white/[0.04] transition-all duration-300"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${500 + index * 100}ms`,
                }}
              >
                <div className="flex items-center gap-2 text-primary mb-1">
                  <span className="group-hover:scale-110 transition-transform duration-300">{stat.icon}</span>
                  <span className="text-2xl md:text-3xl font-bold text-white">{stat.value}</span>
                </div>
                <span className="text-xs md:text-sm text-white/50 group-hover:text-white/70 transition-colors">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Bottom gradient fade */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, #0a0a0a, transparent)',
        }}
      />
    </section>
  );
};
